import { PassouFalhouEnum } from '@mchat-rf/consulta-seguimento/enums-consultaSeguimento'
import {
  hasAnySim,
  isAllFilled,
  isAllNao,
  isPerguntaOutroFilled,
  isPerguntaOutroNao,
  isPerguntaOutroSelected,
  isPerguntaOutroSim,
} from '@mchat-rf/consulta-seguimento/utils-consultaSeguimento'
import { SimNaoEnum } from 'components/form'
import { isEmpty } from 'util/checks'

import {
  ConsultaSeguimentoPergunta05ExemplosEnum,
  ConsultaSeguimentoPergunta05Model,
} from './model-consultaSeguimentoPergunta05'

const pergunta05ExemplosPassouEnumValues = [
  ConsultaSeguimentoPergunta05ExemplosEnum.OLHA_PARA_AS_MAOS,
  ConsultaSeguimentoPergunta05ExemplosEnum.MOVE_OS_DEDOS_QUANDO_BRINCA_DE_ESCONDER_ROSTO,
]
const pergunta05ExemplosFalhouEnumValues = [
  ConsultaSeguimentoPergunta05ExemplosEnum.MOVIMENTA_OS_DEDOS_PERTO_DOS_OLHOS,
  ConsultaSeguimentoPergunta05ExemplosEnum.MANTEM_AS_MAOS_ELEVADAS_PERTO_DOS_OLHOS,
  ConsultaSeguimentoPergunta05ExemplosEnum.MANTEM_AS_MAOS_AO_LADO_DOS_OLHOS,
  ConsultaSeguimentoPergunta05ExemplosEnum.AGITA_AS_MAOS_PERTO_DO_ROSTO,
]

export function isPergunta05ExemplosAllNao(values: ConsultaSeguimentoPergunta05Model): boolean {
  const exemplosAllNao =
    isAllNao(values?.PERGUNTA_05_EXEMPLOS?.PASSOU, pergunta05ExemplosPassouEnumValues) &&
    isAllNao(values?.PERGUNTA_05_EXEMPLOS?.FALHOU, pergunta05ExemplosFalhouEnumValues)

  if (isPerguntaOutroFilled(values?.OUTRO)) {
    return exemplosAllNao && isPerguntaOutroNao(values?.OUTRO)
  }

  return exemplosAllNao
}

export function hasPergunta05ExemplosPassouAnySim(
  value: ConsultaSeguimentoPergunta05Model['PERGUNTA_05_EXEMPLOS']
): boolean {
  return hasAnySim(value?.PASSOU)
}

export function hasPergunta05ExemplosFalhouAnySim(value: ConsultaSeguimentoPergunta05Model): boolean {
  return hasAnySim(value?.PERGUNTA_05_EXEMPLOS?.FALHOU) || isPerguntaOutroSim(value?.OUTRO)
}

export function isPergunta05ExemplosPassouAllNao(
  value: ConsultaSeguimentoPergunta05Model['PERGUNTA_05_EXEMPLOS']
): boolean {
  return isAllNao(value?.PASSOU, pergunta05ExemplosPassouEnumValues)
}

export function isPergunta05ExemplosFalhouAllNao(value: ConsultaSeguimentoPergunta05Model): boolean {
  const isExemplosFalhouAllNao = isAllNao(value?.PERGUNTA_05_EXEMPLOS?.FALHOU, pergunta05ExemplosFalhouEnumValues)

  if (isPerguntaOutroFilled(value?.OUTRO)) {
    return isExemplosFalhouAllNao && isPerguntaOutroNao(value?.OUTRO)
  }

  return isExemplosFalhouAllNao
}

export function isPergunta05ExemplosFalhouAllFilled(value: ConsultaSeguimentoPergunta05Model): boolean {
  const isExemplosFalhouAllFilled = isAllFilled(value?.PERGUNTA_05_EXEMPLOS?.FALHOU, pergunta05ExemplosFalhouEnumValues)

  if (isPerguntaOutroFilled(value?.OUTRO)) {
    return isExemplosFalhouAllFilled && isPerguntaOutroSelected(value?.OUTRO)
  }

  return isExemplosFalhouAllFilled
}

export function prunePergunta05(value: ConsultaSeguimentoPergunta05Model): ConsultaSeguimentoPergunta05Model {
  const pruned: ConsultaSeguimentoPergunta05Model = {
    status: value.status,
    resultado: value.resultado,
    PERGUNTA_05_HEADER: value.PERGUNTA_05_HEADER,
  }

  const isHeaderSim = value.PERGUNTA_05_HEADER === SimNaoEnum.SIM
  const hasFalhouSim = hasPergunta05ExemplosFalhouAnySim(value)

  if (isHeaderSim) {
    pruned.PERGUNTA_05_EXEMPLOS = value.PERGUNTA_05_EXEMPLOS
    pruned.OUTRO = value.OUTRO
  }

  if (isHeaderSim && hasFalhouSim) {
    pruned.PERGUNTA_05_DUAS_VEZES_POR_SEMANA = value.PERGUNTA_05_DUAS_VEZES_POR_SEMANA
  }

  return pruned
}

export function conditionToCalculatePergunta05(values: ConsultaSeguimentoPergunta05Model): boolean {
  if (values.PERGUNTA_05_HEADER === SimNaoEnum.NAO) {
    return true
  }

  const isExemplosPassouAllNao = isPergunta05ExemplosPassouAllNao(values.PERGUNTA_05_EXEMPLOS)
  const hasExemplosPassouAnySim = hasPergunta05ExemplosPassouAnySim(values.PERGUNTA_05_EXEMPLOS)
  const isExemplosFalhouAllFilled = isPergunta05ExemplosFalhouAllFilled(values)
  const isExemplosFilled = (isExemplosPassouAllNao || hasExemplosPassouAnySim) && isExemplosFalhouAllFilled

  if (!isExemplosFilled) {
    return false
  }

  const isExemplosAllNao = isPergunta05ExemplosAllNao(values)
  const hasAnySimExemplosPassou = hasPergunta05ExemplosPassouAnySim(values?.PERGUNTA_05_EXEMPLOS)
  const isExemplosFalhouAllNao = isPergunta05ExemplosFalhouAllNao(values)

  if (isExemplosAllNao || (isExemplosFalhouAllNao && hasAnySimExemplosPassou)) {
    return true
  }

  const hasAnySimExemplosFalhou = hasPergunta05ExemplosFalhouAnySim(values)

  if (hasAnySimExemplosFalhou) {
    return !isEmpty(values?.PERGUNTA_05_DUAS_VEZES_POR_SEMANA)
  }

  return false
}

export function calculateResultadoPergunta05(values: ConsultaSeguimentoPergunta05Model): PassouFalhouEnum | undefined {
  if (values.PERGUNTA_05_HEADER === SimNaoEnum.NAO) {
    return PassouFalhouEnum.PASSOU
  } else {
    const isExemplosAllNao = isPergunta05ExemplosAllNao(values)
    const hasAnySimExemplosPassou = hasPergunta05ExemplosPassouAnySim(values.PERGUNTA_05_EXEMPLOS)
    const isExemplosFalhouAllNao = isPergunta05ExemplosFalhouAllNao(values)

    if ((hasAnySimExemplosPassou && isExemplosFalhouAllNao) || isExemplosAllNao) {
      return PassouFalhouEnum.PASSOU
    }

    if (values.PERGUNTA_05_DUAS_VEZES_POR_SEMANA === SimNaoEnum.SIM) {
      return PassouFalhouEnum.FALHOU
    } else if (values.PERGUNTA_05_DUAS_VEZES_POR_SEMANA === SimNaoEnum.NAO) {
      return PassouFalhouEnum.PASSOU
    }
  }
}
